import { useRef, useState } from "react";
import { ChevronDown, GripVertical, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useQueryClient } from "@tanstack/react-query";
import { resetRevenueMock, resetStatements } from "@/lib/devMock";
import { cn } from "@/lib/utils";
import { ENTRY_VARIANTS, scenarioById, scenariosFor, useScenarioUi } from "./scenarios";

/**
 * Prototype-only dev bar: reset the mock data, and pick which scenario the
 * revenue list is showing.
 *
 * The scenarios are listed here rather than hidden behind a popover, so it is
 * always visible which one is active. Ticking one filters the list to its
 * statements; unticking it puts everything back. Only one can be active at a
 * time, so ticking another swaps.
 *
 * Draggable via the grip handle, and collapsible down to its header — the list
 * is tall, and once a scenario is picked there is nothing to read. The active
 * scenario stays visible while collapsed. Mounted globally but only when the dev
 * auth bypass is active (see App.tsx).
 */
const POS_KEY = "mss-reset-button-pos";
const COLLAPSED_KEY = "mss-dev-bar-collapsed";

function loadPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to default */
  }
  return { x: 12, y: 12 };
}

export function PrototypeDevBar() {
  const queryClient = useQueryClient();
  const [pos, setPos] = useState(loadPos);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "true");
  const posRef = useRef(pos);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);

  const showLabels = useScenarioUi((s) => s.showLabels);
  const setShowLabels = useScenarioUi((s) => s.setShowLabels);
  const showHints = useScenarioUi((s) => s.showHints);
  const setShowHints = useScenarioUi((s) => s.setShowHints);
  const activeScenarioId = useScenarioUi((s) => s.activeScenarioId);
  const setActiveScenario = useScenarioUi((s) => s.setActiveScenario);
  const entryVariant = useScenarioUi((s) => s.entryVariant);
  const setEntryVariant = useScenarioUi((s) => s.setEntryVariant);
  const fullNavigation = useScenarioUi((s) => s.fullNavigation);
  const setFullNavigation = useScenarioUi((s) => s.setFullNavigation);
  const active = scenarioById(activeScenarioId);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const x = Math.max(0, Math.min(window.innerWidth - 120, d.ox + (e.clientX - d.sx)));
    const y = Math.max(0, Math.min(window.innerHeight - 60, d.oy + (e.clientY - d.sy)));
    posRef.current = { x, y };
    setPos(posRef.current);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (drag.current) localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    drag.current = null;
  };

  const toggleCollapsed = () => {
    setCollapsed((wasCollapsed) => {
      localStorage.setItem(COLLAPSED_KEY, String(!wasCollapsed));
      return !wasCollapsed;
    });
  };

  const handleReset = () => {
    resetRevenueMock();
    window.location.reload();
  };

  /**
   * Picking a scenario re-seeds its statements, so it is always walkable from
   * the start — settling them, then coming back, would otherwise leave nothing
   * to do. Only that scenario's data is touched; everything else is left as it
   * was. Unticking just clears the filter.
   */
  const pickScenario = (id: string | null) => {
    const scenario = scenarioById(id);
    if (scenario) resetStatements(scenario.statementIds);
    setActiveScenario(id);
    queryClient.invalidateQueries({ queryKey: ["revenue-statements"] });
  };

  return (
    <div
      // pointer-events-auto: vaul locks pointer events on <body> while a drawer
      // is open, and this panel lives outside the dialog. Without it the bar
      // cannot be dragged or clicked while settling.
      data-prototype-panel
      className="pointer-events-auto fixed z-[100] w-[22rem] rounded-md border border-border bg-background shadow-md"
      style={{ left: pos.x, top: pos.y }}
    >
      <div
        className={cn(
          "flex items-center gap-1 px-1.5 py-1",
          !collapsed && "border-b border-border",
        )}
      >
        <button
          type="button"
          aria-label="Drag to move"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="flex cursor-grab touch-none items-center text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-4" />
        </button>
        <Button type="button" variant="ghost" size="sm" className="pl-2" onClick={handleReset}>
          <RotateCcw className="size-4 text-muted-foreground" />
          Reset prototype
        </Button>

        {/* Collapsed, this is the only reminder of what the list is filtered to. */}
        {collapsed && active && (
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {active.name}
          </span>
        )}

        <button
          type="button"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand prototype controls" : "Collapse prototype controls"}
          onClick={toggleCollapsed}
          className="ml-auto flex items-center rounded-sm p-1 text-muted-foreground hover:bg-subtle hover:text-foreground"
        >
          <ChevronDown className={cn("size-4 transition-transform", collapsed && "-rotate-90")} />
        </button>
      </div>

      {!collapsed && (
        <>
          {/* How a settlement is started. Both ways produce the same payment. */}
          <div className="flex flex-col gap-1.5 border-b border-border px-3 py-2">
            <span className="text-xs font-medium text-muted-foreground">Settle entry</span>
            <div className="flex flex-col gap-1">
              {ENTRY_VARIANTS.map((variant) => (
                <label
                  key={variant.id}
                  htmlFor={`entry-${variant.id}`}
                  className="flex cursor-pointer items-center gap-2.5"
                >
                  <input
                    id={`entry-${variant.id}`}
                    type="radio"
                    name="settle-entry"
                    className="size-4 accent-foreground"
                    checked={entryVariant === variant.id}
                    onChange={() => {
                      setEntryVariant(variant.id);
                      // The scenarios differ per variant, so start clean.
                      setActiveScenario(null);
                    }}
                  />
                  <span className="text-sm">{variant.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-col divide-y divide-border">
            {scenariosFor(entryVariant).map((scenario) => {
              const isActive = scenario.id === activeScenarioId;
              return (
                <label
                  key={scenario.id}
                  htmlFor={`scenario-${scenario.id}`}
                  className="flex cursor-pointer items-start gap-2.5 px-3 py-2 hover:bg-subtle"
                >
                  <Checkbox
                    id={`scenario-${scenario.id}`}
                    className="mt-0.5"
                    checked={isActive}
                    // Only one scenario at a time: ticking another swaps, unticking
                    // the active one shows every statement again.
                    onCheckedChange={(checked) =>
                      pickScenario(checked === true ? scenario.id : null)
                    }
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium leading-snug">{scenario.name}</span>
                    <span className="text-xs leading-snug text-muted-foreground">
                      {scenario.vendorName} —{" "}
                  {entryVariant === "B" && scenario.variantB
                    ? scenario.variantB.tryIt
                    : scenario.entries.map((e) => e.tryIt).join("; ")}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
            <label
              htmlFor="show-scenario-labels"
              className="flex cursor-pointer items-center gap-2.5"
            >
              <Checkbox
                id="show-scenario-labels"
                checked={showLabels}
                onCheckedChange={(checked) => setShowLabels(checked === true)}
              />
              <span className="text-sm">Show scenario labels</span>
            </label>
            <label
              htmlFor="show-testing-hints"
              className="flex cursor-pointer items-center gap-2.5"
            >
              <Checkbox
                id="show-testing-hints"
                checked={showHints}
                onCheckedChange={(checked) => setShowHints(checked === true)}
              />
              <span className="text-sm">Show testing hints</span>
            </label>
            <label
              htmlFor="full-navigation"
              className="flex cursor-pointer items-center gap-2.5"
            >
              <Checkbox
                id="full-navigation"
                checked={fullNavigation}
                onCheckedChange={(checked) => setFullNavigation(checked === true)}
              />
              <span className="text-sm">Show full navigation</span>
            </label>
          </div>
        </>
      )}
    </div>
  );
}
